import { useMemo } from 'react';
import {
  CourseEnrollmentStates,
  CourseValidationType,
} from '../types/CourseValidationType';
import {
  CoursesBySemestersExtraCourse,
  CourseStatusTooltip,
  SemesterCourseWithValidation,
} from '../types/progress';
import Course, { CourseProps } from './Course';

export interface CoursesBySemesterProps {
  title: string;
  texts: {
    credits: string;
    convalidationToolTip: (data: CourseStatusTooltip) => string;
    equivalentToTooltip: (data: { code: string; name: string }) => string;
    degreeActivityType?: string;
    degreeActivityTooltip?: string;
  };
  getColor: (
    state?: CourseEnrollmentStates | CourseValidationType,
  ) => string | undefined;
  onClick?: (semesterCourse: SemesterCourseWithValidation) => void;
  onClickInfoModal?: (row: SemesterCourseWithValidation) => void;
  semesterCourses: SemesterCourseWithValidation[];
  extraCourses?: CoursesBySemestersExtraCourse[];
}

export default function CoursesBySemester({
  title,
  texts,
  getColor,
  onClick: onClickProp,
  onClickInfoModal,
  semesterCourses,
  extraCourses,
}: CoursesBySemesterProps) {
  const courses = useMemo<
    Array<Omit<CourseProps, 'texts'> & { key: number }>
  >(() => {
    return semesterCourses.map((semesterCourse) => {
      let onClick = undefined;
      if (
        onClickProp &&
        (!semesterCourse.state ||
          semesterCourse.state === CourseEnrollmentStates.Reject)
      ) {
        onClick = () => onClickProp(semesterCourse);
      }
      if (
        onClickInfoModal &&
        semesterCourse.state === CourseEnrollmentStates.Approved &&
        semesterCourse.isValidated
      ) {
        onClick = () => onClickInfoModal(semesterCourse);
      }

      if (semesterCourse.course) {
        return {
          key: semesterCourse.id,
          name: semesterCourse.course.name,
          code: semesterCourse.course.code,
          credits: semesterCourse.course.credits,
          color: getColor(
            semesterCourse.state === CourseEnrollmentStates.Approved &&
              semesterCourse.isValidated
              ? semesterCourse?.validation?.validationType ??
                  CourseValidationType.Internal
              : semesterCourse.state,
          ),
          examGrade: semesterCourse.validation?.examGrade,
          finalGrade: semesterCourse.validation?.finalGrade,
          toolTip:
            semesterCourse.state === CourseEnrollmentStates.Approved &&
            semesterCourse.isValidated
              ? texts.convalidationToolTip({
                  validationType:
                    semesterCourse?.validation?.validationType ??
                    CourseValidationType.Internal,
                  validationSubType:
                    semesterCourse?.validation?.validationSubType ?? null,
                  examGrade: semesterCourse.validation?.examGrade,
                  finalGrade: semesterCourse.validation?.finalGrade,
                  externalGrade: semesterCourse.validation?.externalGrade,
                  externalCourseName:
                    semesterCourse.validation?.externalCourseName,
                  code: semesterCourse.validation?.code,
                })
              : semesterCourse.enrolledByEquivalence
              ? texts.equivalentToTooltip({
                  code: semesterCourse.enrolledByEquivalence.code,
                  name: semesterCourse.enrolledByEquivalence.name,
                })
              : '',
          onClick,
        };
      } else {
        return {
          key: semesterCourse.id,
          name: semesterCourse.courseType.name,
          credits: semesterCourse.optionalSlot?.credits,
          color: getColor(semesterCourse.state),
          examGrade: semesterCourse.validation?.examGrade,
          finalGrade: semesterCourse.validation?.finalGrade,
          toolTip: '',
          onClick,
        };
      }
    });
  }, [onClickProp, onClickInfoModal, semesterCourses, getColor, texts]);

  return (
    <div>
      <div className="d-block text-center mb-3 px-1">{title}</div>
      <div
        className="d-flex"
        style={{
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {courses.map((course) => (
          <Course {...course} texts={texts} />
        ))}
        {extraCourses?.map((extraCourse) => {
          const id = Math.random().toString(36).substr(2, 9);
          return (
            <Course
              name={extraCourse.name}
              color={getColor(
                isCourseStatusType(extraCourse.state)
                  ? extraCourse.state
                  : undefined,
              )}
              texts={texts}
              replaceCredits={texts.degreeActivityType}
              toolTip={texts.degreeActivityTooltip}
              toolTipId={`extracourse-${id}`}
            />
          );
        })}
      </div>
    </div>
  );
}

function isCourseStatusType(value?: CourseEnrollmentStates) {
  const validStatuses = Object.values(CourseEnrollmentStates);
  return validStatuses.includes(value as CourseEnrollmentStates);
}
